import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

/**
 * PresenterMode — overlay grid of all scenes.
 * Click or press Enter to jump; Esc to close.
 */
export const PresenterMode = ({
  open,
  scenes,
  index,
  onJump,
  onClose,
}) => {
  return (
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
          className="absolute inset-0 z-50 bg-black/85 backdrop-blur-2xl"
          data-testid="presenter-mode"
        >
          <div className="h-full w-full flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between px-10 py-7 border-b border-white/10">
              <div>
                <div className="kn-eyebrow">Presenter overview</div>
                <h2 className="kn-display text-2xl mt-1 text-white">
                  All scenes
                  <span
                    className="ml-3 text-white/40 text-base font-normal tracking-normal"
                  >
                    {scenes.length} slides · press G to toggle · Esc to close
                  </span>
                </h2>
              </div>
              <button
                type="button"
                onClick={onClose}
                aria-label="Close presenter mode"
                className="w-10 h-10 rounded-full glass flex items-center justify-center text-white/80 hover:text-white hover:bg-white/10 transition-colors"
                data-testid="presenter-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Grid */}
            <div className="presenter-grid flex-1 overflow-y-auto px-10 py-10">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {scenes.map((s, i) => {
                  const isActive = i === index;
                  const Thumb = s.component;
                  const handleKeyDown = (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onJump?.(i);
                    }
                  };
                  return (
                    <motion.div
                      key={s.id}
                      role="button"
                      tabIndex={0}
                      aria-label={`Jump to slide ${i + 1}: ${s.title}`}
                      onClick={() => onJump?.(i)}
                      onKeyDown={handleKeyDown}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{
                        delay: i * 0.04,
                        duration: 0.6,
                        ease: [0.16, 1, 0.3, 1],
                      }}
                      whileHover={{ y: -4 }}
                      className={`group text-left rounded-2xl overflow-hidden border transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-lime ${
                        isActive
                          ? "border-lime ring-2 ring-lime/40"
                          : "border-white/10 hover:border-white/30"
                      }`}
                      data-testid={`presenter-thumb-${i}`}
                    >
                      <div
                        className="relative w-full overflow-hidden bg-black pointer-events-none"
                        style={{ aspectRatio: "16/9" }}
                        aria-hidden="true"
                      >
                        {/* Live mini preview at scaled size */}
                        <div
                          className="absolute top-0 left-0 origin-top-left pointer-events-none"
                          style={{
                            width: "1920px",
                            height: "1080px",
                            transform: "scale(0.18)",
                          }}
                        >
                          {Thumb ? <Thumb {...(s.props || {})} /> : null}
                        </div>
                        {/* Gradient overlay for legibility */}
                        <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
                        <div className="absolute top-3 left-3 text-[10px] tracking-[0.32em] uppercase text-white/80 bg-black/40 backdrop-blur px-2 py-1 rounded">
                          {String(i + 1).padStart(2, "0")}
                        </div>
                      </div>
                      <div className="px-4 py-3 bg-white/[0.02]">
                        <div className="text-sm font-semibold text-white truncate">
                          {s.title}
                        </div>
                        <div className="text-xs text-white/55 mt-0.5 truncate">
                          {s.subtitle || s.id}
                        </div>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
};

export default PresenterMode;
